import React from "react";
import { useI18n } from "@/context/I18nContext";
import { Sparkles, TrendingUp, Target, Percent, Timer } from "lucide-react";

const REV = [0.5, 0.62, 0.78, 0.9, 1.0];
const EBITDA = [0.2, 0.26, 0.33, 0.4, 0.46];
const NET = [0.09, 0.13, 0.18, 0.23, 0.28];
const YEARS = ["’21", "’22", "’23", "’24", "’25"];
const RATIOS = [
  { key: "hv_liq", v: 78, color: "#2563EB" },
  { key: "hv_sol", v: 71, color: "#3B82F6" },
  { key: "hv_red", v: 88, color: "#22D3EE" },
  { key: "hv_cov", v: 82, color: "#0EA5E9" },
];

// Analysis report mock visual (replaces the stock photo in the Method section)
export function MethodVisual() {
  const { t } = useI18n();
  return (
    <div className="relative animate-fade-up">
      <div className="absolute -inset-6 rounded-[2rem] bg-blue-600/10 blur-3xl" aria-hidden="true" />
      <div className="relative rounded-2xl border border-slate-800 bg-slate-900/90 backdrop-blur-sm shadow-xl p-6 sm:p-7" data-testid="method-visual">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-[11px] font-semibold tracking-[0.18em] uppercase text-slate-500">{t("mv_report")}</p>
            <p className="mt-1 font-heading text-lg font-bold text-white">Acme Manifatture S.r.l.</p>
          </div>
          <div className="flex items-center gap-2">
            <div className="text-right">
              <span className="font-mono-fin text-xl font-bold text-white leading-none">84</span>
              <p className="text-[9px] uppercase tracking-widest text-slate-500">score</p>
            </div>
            <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-blue-800 to-blue-600 flex items-center justify-center shadow-lg shadow-blue-600/20">
              <span className="font-heading text-2xl font-extrabold text-white">A</span>
            </div>
          </div>
        </div>

        {/* Grouped bar chart */}
        <p className="mt-6 text-xs font-semibold text-slate-400">{t("mv_trend")}</p>
        <div className="mt-3 flex items-end gap-2 sm:gap-3 h-36" data-testid="method-bars">
          {YEARS.map((y, i) => (
            <div key={y} className="flex-1 flex flex-col items-center gap-1.5">
              <div className="w-full flex items-end justify-center gap-[3px] h-32">
                <div className="w-1/3 rounded-t bg-blue-600 animate-fade-up" style={{ height: `${REV[i] * 100}%`, animationDelay: `${i * 70}ms` }} />
                <div className="w-1/3 rounded-t bg-cyan-400 animate-fade-up" style={{ height: `${EBITDA[i] * 100}%`, animationDelay: `${i * 70 + 40}ms` }} />
                <div className="w-1/3 rounded-t bg-emerald-500 animate-fade-up" style={{ height: `${NET[i] * 100}%`, animationDelay: `${i * 70 + 80}ms` }} />
              </div>
              <span className="text-[10px] text-slate-500 font-mono-fin">{y}</span>
            </div>
          ))}
        </div>
        <div className="mt-3 flex items-center gap-4 text-[10px] text-slate-400">
          <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-blue-600" /> {t("m_ricavi")}</span>
          <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-cyan-400" /> {t("m_ebitda")}</span>
          <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-emerald-500" /> {t("m_utile")}</span>
        </div>

        {/* Ratio bars */}
        <div className="mt-6 grid grid-cols-2 gap-x-5 gap-y-2.5 border-t border-slate-800 pt-5">
          {RATIOS.map((b, i) => (
            <div key={b.key} className="flex items-center gap-2">
              <span className="w-16 text-[10px] text-slate-400 shrink-0">{t(b.key)}</span>
              <div className="flex-1 h-1.5 rounded-full bg-slate-800 overflow-hidden">
                <div className="h-full rounded-full animate-fade-up" style={{ width: `${b.v}%`, backgroundColor: b.color, animationDelay: `${200 + i * 80}ms` }} />
              </div>
              <span className="w-6 text-right font-mono-fin text-[10px] text-slate-300">{b.v}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// Business plan projection mock visual
const BP_REV = [0.35, 0.5, 0.68, 0.85, 1.0];
const BP_FCF = [-0.4, -0.12, 0.2, 0.58, 0.92]; // cumulative FCF, crosses zero
export function BusinessPlanVisual() {
  const { t } = useI18n();
  const W = 320, H = 170, padL = 12, padR = 12, baseY = 150, topY = 16;
  const slot = (W - padL - padR) / BP_REV.length;
  const barW = slot * 0.5;
  const zeroY = 118, fcfScale = 74;
  const bars = BP_REV.map((v, i) => {
    const x = padL + i * slot + (slot - barW) / 2;
    const h = v * (baseY - topY);
    return { x, y: baseY - h, w: barW, h };
  });
  const fcfPts = BP_FCF.map((v, i) => {
    const x = padL + i * slot + slot / 2;
    const y = zeroY - v * fcfScale;
    return [x, y];
  });
  const linePath = fcfPts.map((p, i) => `${i === 0 ? "M" : "L"} ${p[0].toFixed(1)} ${p[1].toFixed(1)}`).join(" ");
  const kpis = [
    { icon: Target, label: t("bl_kpi_npv"), value: "€412k", color: "text-emerald-400" },
    { icon: Percent, label: t("bl_kpi_irr"), value: "22%", color: "text-blue-400" },
    { icon: Timer, label: t("bl_kpi_be"), value: "18 " + (t("year_label") === "Anno" ? "mesi" : "mo"), color: "text-cyan-400" },
  ];

  return (
    <div className="relative animate-fade-up" style={{ animationDelay: "120ms" }}>
      <div className="absolute -inset-6 rounded-[2rem] bg-cyan-400/10 blur-3xl" aria-hidden="true" />
      <div className="relative rounded-2xl border border-slate-800 bg-slate-900/90 backdrop-blur-sm shadow-xl p-6 sm:p-7" data-testid="bp-visual">
        <div className="flex items-center justify-between">
          <p className="inline-flex items-center gap-1.5 text-sm font-semibold text-blue-400"><TrendingUp className="h-4 w-4" /> Business Plan</p>
          <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-cyan-300 bg-cyan-500/10 border border-cyan-500/20 rounded-full px-2 py-0.5">
            <Sparkles className="h-3 w-3" /> AI
          </span>
        </div>

        {/* KPI chips */}
        <div className="mt-4 grid grid-cols-3 gap-2.5">
          {kpis.map((k) => (
            <div key={k.label} className="rounded-lg border border-slate-800 bg-slate-950/60 p-3">
              <div className="flex items-center gap-1 text-[9px] uppercase tracking-wider text-slate-500">
                <k.icon className="h-3 w-3" /> {k.label}
              </div>
              <p className={`mt-1 font-mono-fin text-base font-bold ${k.color}`}>{k.value}</p>
            </div>
          ))}
        </div>

        {/* Projection chart */}
        <p className="mt-5 text-xs font-semibold text-slate-400">{t("bl_proj")}</p>
        <svg viewBox={`0 0 ${W} ${H}`} className="mt-2 w-full h-44" preserveAspectRatio="none" aria-hidden="true">
          <defs>
            <linearGradient id="bpBar" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#3B82F6" />
              <stop offset="100%" stopColor="#1E40AF" />
            </linearGradient>
          </defs>
          {/* zero line */}
          <line x1={padL} y1={zeroY} x2={W - padR} y2={zeroY} stroke="#334155" strokeWidth="1" strokeDasharray="4 4" />
          {bars.map((b) => (
            <rect key={`bar-${b.x.toFixed(0)}`} x={b.x} y={b.y} width={b.w} height={b.h} rx="3" fill="url(#bpBar)" opacity="0.9" />
          ))}
          {/* FCF line */}
          <path d={linePath} fill="none" stroke="#22D3EE" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
          {fcfPts.map((p) => (
            <circle key={`pt-${p[0].toFixed(0)}`} cx={p[0]} cy={p[1]} r="3.5" fill="#0F172A" stroke="#22D3EE" strokeWidth="2" />
          ))}
        </svg>
        <div className="mt-1 flex items-center justify-between px-1">
          {["Y1", "Y2", "Y3", "Y4", "Y5"].map((y) => <span key={y} className="text-[10px] text-slate-500 font-mono-fin">{y}</span>)}
        </div>
        <div className="mt-3 flex items-center gap-4 text-[10px] text-slate-400">
          <span className="flex items-center gap-1.5"><span className="h-2 w-3 rounded-sm bg-blue-600" /> {t("bl_rev")}</span>
          <span className="flex items-center gap-1.5"><span className="h-2 w-3 rounded-sm bg-cyan-400" /> {t("bl_fcf")}</span>
        </div>
      </div>
    </div>
  );
}
